(() => {
  if (window.PMLocalizedMoney) return;

  const LOCALIZED_MONEY_SELECTOR = '[data-localized-money="true"]';
  const observedRoots = new Set();
  let observer = null;
  let scheduled = false;

  function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function normalizeLanguageCode(value) {
    return normalizeString(value).replace(/_/g, '-');
  }

  function normalizeCountryCode(value) {
    const normalized = normalizeString(value).toUpperCase();
    return normalized === 'UK' ? 'GB' : normalized;
  }

  function getLocaleCandidates(languageCode = '', countryCode = '') {
    const locales = [];
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    const normalizedCountryCode = normalizeCountryCode(countryCode);

    const addLocale = (locale) => {
      const normalizedLocale = normalizeLanguageCode(locale);
      if (normalizedLocale && !locales.includes(normalizedLocale)) {
        locales.push(normalizedLocale);
      }
    };

    if (normalizedLanguageCode) {
      const parts = normalizedLanguageCode.split('-').filter(Boolean);
      const language = parts[0]?.toLowerCase();
      let script = '';

      if (parts.length > 1 && parts[1].length === 4) {
        script = `${parts[1].slice(0, 1).toUpperCase()}${parts[1].slice(1).toLowerCase()}`;
      }

      if (language && normalizedCountryCode) {
        addLocale(script ? `${language}-${script}-${normalizedCountryCode}` : `${language}-${normalizedCountryCode}`);
      }

      addLocale(normalizedLanguageCode);

      if (language) {
        addLocale(language);
      }
    }

    addLocale(document.documentElement.lang);
    addLocale(window.navigator?.language);

    return locales.length ? locales : undefined;
  }

  function formatFallback(amount, currencyCode, fallback) {
    if (fallback) return fallback;

    if (!Number.isFinite(amount)) return '';

    if (currencyCode) {
      return `${amount.toFixed(2)} ${currencyCode}`;
    }

    return amount.toFixed(2);
  }

  function formatAmount(amount, options = {}) {
    const numericAmount = Number(amount);
    const normalizedCurrencyCode = normalizeString(options.currencyCode).toUpperCase();
    const fallback = normalizeString(options.fallback);

    if (!Number.isFinite(numericAmount)) {
      return formatFallback(numericAmount, normalizedCurrencyCode, fallback);
    }

    if (!normalizedCurrencyCode) {
      return formatFallback(numericAmount, normalizedCurrencyCode, fallback);
    }

    try {
      return new Intl.NumberFormat(getLocaleCandidates(options.languageCode, options.countryCode), {
        style: 'currency',
        currency: normalizedCurrencyCode,
        currencyDisplay: options.withCurrency ? 'code' : 'symbol',
      }).format(numericAmount);
    } catch (error) {
      return formatFallback(numericAmount, normalizedCurrencyCode, fallback);
    }
  }

  function formatCents(cents, options = {}) {
    const numericCents = Number(cents);
    if (!Number.isFinite(numericCents)) {
      return formatFallback(Number.NaN, normalizeString(options.currencyCode).toUpperCase(), options.fallback);
    }

    return formatAmount(numericCents / 100, options);
  }

  function formatElement(element, nextCents = null) {
    if (!(element instanceof Element)) return '';

    const cents = nextCents === null ? Number(element.dataset.cents) : Number(nextCents);
    const formatted = formatCents(cents, {
      currencyCode: element.dataset.currencyCode,
      countryCode: element.dataset.countryCode,
      languageCode: element.dataset.languageCode,
      withCurrency: element.dataset.moneyWithCurrency === 'true',
      fallback: element.dataset.fallback || element.textContent,
    });

    if (nextCents !== null && Number.isFinite(cents)) {
      element.dataset.cents = `${Math.round(cents)}`;
    }

    if (formatted !== '') {
      element.textContent = formatted;
    }

    return formatted;
  }

  function getLocalizedMoneyNodes(root = document) {
    if (!(root instanceof Element) && root !== document) return [];

    const nodes = [];

    if (root instanceof Element && root.matches(LOCALIZED_MONEY_SELECTOR)) {
      nodes.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      nodes.push(...root.querySelectorAll(LOCALIZED_MONEY_SELECTOR));
    }

    return nodes;
  }

  function formatAll(root = document) {
    getLocalizedMoneyNodes(root).forEach((node) => {
      formatElement(node);
    });
  }

  function flushObservedRoots() {
    scheduled = false;
    if (!observedRoots.size) return;

    const roots = Array.from(observedRoots);
    observedRoots.clear();

    roots.forEach((root) => formatAll(root));
  }

  function scheduleFormat(root) {
    observedRoots.add(root);

    if (scheduled) return;

    scheduled = true;
    window.requestAnimationFrame(flushObservedRoots);
  }

  function startObserver() {
    if (observer || !document.body) return;

    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element || node === document) {
            scheduleFormat(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function initialize() {
    formatAll(document);
    startObserver();
  }

  window.PMLocalizedMoney = {
    formatAmount,
    formatCents,
    formatElement,
    formatAll,
    getLocaleCandidates,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
